import ProjectInvoice from '../model/invoice/project-invoice';
import { Project } from '../model/project/project';
import ProductHelper from './product-helper';
import * as moment from 'moment-timezone';
import DateHelper from './date-helper';
import { Adjustment } from '../model/project/adjustment';
import { BaseProject } from '../model/project/base-project';
import { SUB_PROJECT_TYPES, SubProject } from '../model/project/sub-project';
import { ProjectPlacement } from '../model/project/projectPlacement';
import Address from '../model/project/address';
import ProjectContact from '../model/company/projectContact';
import Company from '../model/company/company';
import { ProductTypes } from '../model/product/product-type';
import { ProjectDto } from '../model/project/projectDto';
import { User } from '../model/user';
import { CollectionHelper } from './collection-helper';
import ProductAsset from '../model/product-asset';
import { PriceLine } from '../model/price-line';
import AddressWithType from '../model/project/address-with-type';
import AddressHelper from './address-helper';
import { StringHelper } from './string-helper';
import Employee from '../model/product/employee';
import { PrintAddressPipe } from '../pipes/print-address.pipe';
import IPBoard from '../model/product/ip-board/ip-board';
import IPBoardPart from '../model/product/ip-board/ip-board-part';
import { IPBoardPlacementTypeChoices } from '../model/product/ip-board/ip-board-placement-type';
import { STATUS } from '../model/project/status';
import EmployeeHelper from './employee-helper';
import { KeysOfType } from './utility-functions';

export default class ProjectHelper {
  public static EXECUTED_FOR_CLIENT_STATUSES = [
    'Uitgevoerd',
    'Te_factureren',
    'Gearchiveerd',
  ];
  public static NOT_EXECUTED_STATUSES = ['Deels_factureren', 'Niet_uitgevoerd'];
  private static ADJUSTMENT_DESIGN_ASSETS = [
    'adjustmentDesign',
    'adjustmentInternal',
    'adjustmentExternal',
  ];
  private static STATUS_TO_CLIENT_DISPLAY_STATUS: Map<string, string> = new Map<
    string,
    string
  >([
    [STATUS.DRAFT, STATUS.NEW_PROJECT],
    [STATUS.NEW_PROJECT, STATUS.NEW_PROJECT],
    [STATUS.COMPLETE, STATUS.COMPLETE],
    [STATUS.INVOICE, STATUS.COMPLETE],
    [STATUS.DESIGN, STATUS.DESIGN],
    [STATUS.IN_PROGRESS, STATUS.IN_PROGRESS.replace('_', ' ')],
    [STATUS.ARCHIVED, STATUS.ARCHIVED],
    [STATUS.DELAYED, STATUS.DELAYED],
  ]);
  private static PLACEMENT_FLOOR_APPOINTMENT_NOT_OBLIGATORY: string[] = [
    'Gelijkvloers',
    'Tussen 0 - 1',
    '1ste verdieping',
    'Tussen 1 - 2',
  ];

  static splitEvenly(subProject: SubProject) {
    if (subProject != null) {
      for (let inv of subProject.projectInvoices) {
        if (inv.priceLines == null) {
          continue;
        }
        for (let line of inv.priceLines) {
          if (line.placementLine) {
            return false;
          }
        }
      }
    }
    return true;
  }

  static getSubtotalOfInvoice(
    invoice: ProjectInvoice,
    project: Project
  ): number {
    let result = 0;
    for (let p of project.products) {
      result += ProductHelper.getPrice(p, invoice, project);
      for (let line of ProductHelper.getPriceLinesOfProductAndInvoiceInvoice(
        p.id,
        invoice
      )) {
        result += Number(line.price);
      }
    }
    return result;
  }

  static getVAT(invoice: ProjectInvoice, project: Project): number {
    let result = 0;
    result += ProjectHelper.getSubtotalOfInvoice(invoice, project);
    result = (result / 100) * 21;
    return result;
  }

  static getAdjustmentInvoiceSubTotal(invoice: ProjectInvoice): number {
    return invoice == null || !CollectionHelper.isPopulated(invoice.priceLines)
      ? 0
      : this.reducePricelines(invoice.priceLines);
  }

  private static reducePricelines(priceLines: PriceLine[]): number {
    let price = 0;
    priceLines.forEach((pl) => {
      price += pl.price == null ? 0 : Number(pl.price);
    });
    return price;
  }

  static getMostRecentPlacementDate(project: Project): any {
    let toReturn = null;
    if (project.products != null && project.products.length > 0) {
      for (let product of project.products) {
        if (
          product.productPlacement != null &&
          product.productPlacement.executionDate != null
        ) {
          if (toReturn == null) {
            toReturn = moment.tz(
              product.productPlacement.executionDate,
              'Europe/Brussels'
            );
          } else {
            toReturn = ProjectHelper.max(
              toReturn,
              moment.tz(
                product.productPlacement.executionDate,
                'Europe/Brussels'
              )
            );
          }
        }
      }
    }

    return toReturn;
  }

  static max(d1: any, d2: any): any {
    return d1 < d2 ? d2 : d1;
  }

  static getCurrentRequestDate(subProject: SubProject) {
    if (subProject.currentRequestDate == null) {
      return subProject.currentRequestASAP ? 'ASAP' : '-';
    } else {
      return DateHelper.formatDate(subProject.currentRequestDate);
    }
  }

  static getCurrentPrintDate(subProject: SubProject) {
    if (subProject.currentPrintDate == null) {
      return subProject.currentPrintASAP ? 'ASAP' : '-';
    } else {
      return DateHelper.formatDate(subProject.currentPrintDate);
    }
  }

  static getCurrentFinishDate(subProject: SubProject) {
    if (subProject.currentFinishDate == null) {
      return subProject.currentFinishASAP ? 'ASAP' : '-';
    } else {
      return DateHelper.formatDate(subProject.currentFinishDate);
    }
  }

  static getCurrentDesignDate(subProject: SubProject) {
    if (subProject.currentRequestDate == null) {
      return subProject.currentRequestASAP ? 'ASAP' : '-';
    } else {
      return DateHelper.formatDate(subProject.currentRequestDate);
    }
  }

  static getCurrentExecutionDate(subProject: SubProject) {
    return subProject.currentExecutionDate == null
      ? ''
      : DateHelper.formatDate(subProject.currentExecutionDate);
  }

  static getCurrentCreatedAt(subProject: SubProject) {
    return subProject.currentCreatedAt == null
      ? ''
      : DateHelper.formatDate(subProject.currentCreatedAt);
  }

  static getCurrentConfirmedPlacementDate(subProject: SubProject) {
    return subProject.currentConfirmedPlacementDate == null
      ? ''
      : DateHelper.formatDate(subProject.currentConfirmedPlacementDate);
  }

  static getDisplayPlacementDate(subProject: SubProject) {
    return this.EXECUTED_FOR_CLIENT_STATUSES.includes(subProject.status)
      ? this.getCurrentExecutionDate(subProject)
      : this.getCurrentConfirmedPlacementDate(subProject);
  }

  static isPlacementEarliestDateValidForClient(
    subProject: SubProject,
    minDate: Date
  ): boolean {
    return (
      subProject.projectPlacement == null ||
      subProject.projectPlacement.placeASAP ||
      (subProject.projectPlacement.placementEarliestDate != null &&
        subProject.projectPlacement.placementEarliestDate >= minDate)
    );
  }

  static maxAccordingToDate(
    a: SubProject,
    b: SubProject,
    dateProp: KeysOfType<SubProject, Date>
  ): SubProject {
    if (a == null && b == null) {
      return null;
    }
    if (a == null) {
      return b;
    }
    if (b == null) {
      return a;
    }

    const aDateRaw = a[dateProp];
    const bDateRaw = b[dateProp];

    if (aDateRaw == null && bDateRaw == null) {
      return null;
    }
    if (aDateRaw == null) {
      return b;
    }
    if (bDateRaw == null) {
      return a;
    }

    const aMoment = moment(aDateRaw);
    const bMoment = moment(bDateRaw);

    return moment.max(aMoment, bMoment) === aMoment ? a : b;
  }

  static getClientDisplayStatus(subProject: SubProject): string {
    if (subProject == null || subProject.status == null) {
      return null;
    }
    const lookup = this.STATUS_TO_CLIENT_DISPLAY_STATUS.get(subProject.status);
    return lookup == null ? subProject.status : lookup;
  }

  static getBaseProject(project: Project): BaseProject {
    return project.subProjects.filter((sub) => sub.type === 'BaseProject')[0];
  }

  static getLatestAdjustmentOrBaseProject(project: Project): SubProject {
    const adjustments = project.subProjects.filter((sub) => {
      return (
        sub.type === 'Adjustment' &&
        sub.projectPlacement.projectPlacementAssets.some((asset) => {
          return asset.formSection === 'adjustmentPlacementSuccess';
        })
      );
    });

    return adjustments.length > 0
      ? adjustments[adjustments.length - 1]
      : this.getBaseProject(project);
  }

  static hasBaseProject(project: Project): boolean {
    if (project.subProjects != null) {
      for (let sub of project.subProjects) {
        if (sub.type === 'BaseProject') {
          return true;
        }
      }
    }
    return false;
  }

  static getIndexBaseProject(project: Project): number {
    let i = 0;
    if (project.subProjects != null) {
      for (let sub of project.subProjects) {
        if (sub.type === 'BaseProject') {
          return i;
        }
        i++;
      }
    }
    return null;
  }

  static setBaseProject(
    project: Project,
    baseProject: BaseProject,
    overwriteExisting = false
  ): Project {
    if (project.subProjects == null) {
      project.subProjects = [];
    }
    if (project.subProjects.length === 0) {
      project.subProjects.push(new BaseProject());
    }
    if (!ProjectHelper.hasBaseProject(project)) {
      project.subProjects.push(new BaseProject());
    } else if (
      ProjectHelper.getIndexBaseProject(project) != null &&
      overwriteExisting
    ) {
      project.subProjects[ProjectHelper.getIndexBaseProject(project)] =
        baseProject;
    } else if (
      ProjectHelper.getIndexBaseProject(project) == null &&
      ProjectHelper.hasBaseProject(project)
    ) {
      console.error('Project has a baseProject but index is not found');
    }
    return project;
  }

  static getLatestSubProject(p: Project): SubProject {
    let latest = ProjectHelper.getBaseProject(p).createdAt;
    let result: SubProject;
    for (let sub of p.subProjects) {
      if (
        (!sub.archived && result == null) ||
        latest == null ||
        sub.createdAt > latest
      ) {
        latest = sub.createdAt;
        result = sub;
      }
    }
    return result;
  }

  static getSubProjectTypeDisplay(type: string): string {
    switch (type) {
      case SUB_PROJECT_TYPES.BASE_PROJECT:
        return 'Aanvraag';
      case SUB_PROJECT_TYPES.ADJUSTMENT:
        return 'Aanpassing';
      case SUB_PROJECT_TYPES.SOLDRENTED:
        return 'Verkocht/Verhuurd';
      case SUB_PROJECT_TYPES.REPO:
        return 'Weghalen';
      default:
        throw new Error(`unrecognized subproject type: ${type}`);
    }
  }

  static getSubProjectTypeTitle(
    model: SubProject,
    client = false,
    existing = false,
    isIPBoardProject = false
  ): string {
    if (model.type == 'Adjustment') {
      return 'Aanpassing';
    } else if (model.type == 'SoldRented') {
      return 'Verkocht/Verhuurd';
    } else if (model.type == 'Repo') {
      return 'Weghalen';
    } else if (model.type == 'BaseProject') {
      return client
        ? existing
          ? 'Bestelling'
          : 'Nieuwe bestelling'
        : isIPBoardProject
        ? 'Job'
        : 'Project';
    } else {
      console.error('No content component class found for model', model);
      return 'Subproject';
    }
  }

  static getFormDisplayNameUpperCase(model: SubProject): string {
    if (model.type == 'Adjustment') {
      return 'Aanpassing';
    } else if (model.type == 'SoldRented') {
      return 'Verkocht/Verhuurd';
    } else if (model.type == 'Repo') {
      return 'Weghalen';
    } else if (model.type == 'BaseProject') {
      return 'Project';
    } else {
      console.error('No content component class found for model', model);
      return 'Subproject';
    }
  }

  static getFormDisplayNameLowerCase(model: SubProject): string {
    if (model.type == 'Adjustment') {
      return 'aanpassing';
    } else if (model.type == 'SoldRented') {
      return 'verkocht/verhuurd aanpassing';
    } else if (model.type == 'Repo') {
      return 'weghalen';
    } else if (model.type == 'BaseProject') {
      return 'project';
    } else {
      console.error('No content component class found for model', model);
      return 'subproject';
    }
  }

  static getFormDisplayArticle(model: SubProject): string {
    if (model.type == 'Adjustment') {
      return 'de';
    } else if (model.type == 'SoldRented') {
      return 'de';
    } else if (model.type == 'Repo') {
      return 'het';
    } else if (model.type == 'BaseProject') {
      return 'het';
    } else {
      console.error('No content component class found for model', model);
      return 'de';
    }
  }

  static getFormDisplayDemonstrative(model: SubProject): string {
    if (model.type == 'Adjustment') {
      return 'deze';
    } else if (model.type == 'SoldRented') {
      return 'deze';
    } else if (model.type == 'Repo') {
      return 'dit';
    } else if (model.type == 'BaseProject') {
      return 'dit';
    } else {
      console.error('No content component class found for model', model);
      return 'deze';
    }
  }

  static getType(model, project?: Project, showExecuted = true): string {
    const type =
      project != null && project.typeMostRecentlyExecuted != null
        ? project.typeMostRecentlyExecuted
        : model.type;
    if (type == 'Adjustment') {
      return 'Aanpassing';
    } else if (type == 'SoldRented') {
      return 'Verkocht/Verhuurd';
    } else if (type == 'Repo') {
      return 'Weghalen';
    } else if (type == 'BaseProject') {
      return model.status === 'Uitgevoerd' && showExecuted
        ? 'Uitgevoerd'
        : this.getNewLabel(model);
    } else {
      return 'Subproject';
    }
  }

  static getTypeStatusString(
    model,
    project?: Project,
    showExecuted = true
  ): string {
    const type =
      project != null && project.typeMostRecentlyExecuted != null
        ? project.typeMostRecentlyExecuted
        : model.type;
    if (type == 'Adjustment') {
      return 'aanpassing';
    } else if (type == 'SoldRented') {
      return 'verkocht-verhuurd';
    } else if (type == 'Repo') {
      return 'weghalen';
    } else if (type == 'BaseProject') {
      return model.status === 'Uitgevoerd' && showExecuted
        ? 'uitgevoerd'
        : this.getNewLabel(model).toLowerCase();
    } else {
      return 'subproject';
    }
  }

  static getNewLabel(model): string {
    if (model.withPlacement === true) {
      return 'Plaatsing';
    }
    if (model.withPlacement === false) {
      return 'Afhaling';
    }
    return 'Nieuw';
  }

  static getSubWithId(project: Project, subId: Number): SubProject {
    for (let sub of project.subProjects) {
      if (sub.id === subId) {
        return sub;
      }
    }
  }

  static getSubIndexWithId(project: Project, subId: Number): number {
    let i = 0;
    for (let sub of project.subProjects) {
      if (sub.id === subId) {
        return i;
      }
      i++;
    }
  }

  static initBaseProject(project: Project): Project {
    if (this.getIndexBaseProject(project) != null) {
      let model = new BaseProject();
      model.projectPlacement = new ProjectPlacement();
      model.projectPlacement.address = new Address();
      model.projectPlacement.address.country = 'België';
      return this.setBaseProject(project, model, true);
    }
  }

  static copySubProjectQuotationSiteVisitPending(
    copyFrom: SubProject,
    copyTo: SubProject
  ) {
    if (copyFrom == null || copyTo == null) {
      return;
    }

    if (copyFrom.type === 'BaseProject' && copyTo.type === 'BaseProject') {
      (<BaseProject>copyTo).includeRepoInPrice = (<BaseProject>(
        copyFrom
      )).includeRepoInPrice;
    }

    // quotation variables
    copyTo.needsQuotation = copyFrom.needsQuotation;
    copyTo.quotation = copyFrom.quotation;
    copyTo.quotationHandler = copyFrom.quotationHandler;
    copyTo.quotationImportant = copyFrom.quotationImportant;
    copyTo.quotationComments = copyFrom.quotationComments;
    copyTo.quotationSent = copyFrom.quotationSent;

    // site visit variables
    if (copyTo.projectPlacement == null) {
      copyTo.projectPlacement = copyFrom.projectPlacement;
    } else {
      if (copyTo.projectPlacement.id == null) {
        copyTo.projectPlacement.id = copyFrom.projectPlacement.id;
      }
      copyTo.projectPlacement.siteVisit = copyFrom.projectPlacement.siteVisit;
      copyTo.projectPlacement.employee = copyFrom.projectPlacement.employee;
      copyTo.projectPlacement.deadlineSiteVisit =
        copyFrom.projectPlacement.deadlineSiteVisit;
      copyTo.projectPlacement.extraInfoSiteVisit =
        copyFrom.projectPlacement.extraInfoSiteVisit;
    }
    copyTo.siteVisitImportant = copyFrom.siteVisitImportant;
  }

  static getDisplayIndexForSubProject(
    project: Project,
    subProject: SubProject
  ) {
    if (subProject.type === 'BaseProject' && project.id != null) {
      return project.id;
    }
    if (
      project == null ||
      project.subProjects == null ||
      project.subProjects.length === 0 ||
      subProject.type == null
    ) {
      return null;
    }
    let typeCount = 0;
    for (let projSubProject of project.subProjects) {
      if (projSubProject.archived) {
        continue;
      }
      if (projSubProject.id === subProject.id) {
        return typeCount + 1;
      }
      if (projSubProject.type === subProject.type) {
        typeCount++;
      }
    }
    return null;
  }

  static getInvolvedCompanies(project: Project): Company[] {
    if (project == null || project.projectContacts == null) {
      return null;
    }
    let toReturn = project.projectContacts
      .filter((pc) => pc.company != null)
      .map((pc) => pc.company);
    return toReturn.filter(
      (c, i) => toReturn.findIndex((c1) => c.id === c1.id) === i
    );
  }

  static getDtoInvolvedCompanies(project: ProjectDto): Company[] {
    if (project == null || project.projectContacts == null) {
      return null;
    }
    let toReturn = project.projectContacts
      .filter((pc) => pc.company != null)
      .map((pc) => pc.company);
    return toReturn.filter(
      (c, i) => toReturn.findIndex((c1) => c.id === c1.id) === i
    );
  }

  static getUniqueCompaniesFromProjectContacts(
    projectContacts: ProjectContact[]
  ): number[] {
    if (projectContacts == null) {
      return null;
    }
    let toReturn = projectContacts
      .filter((pc) => pc.company != null && pc.company.id != null)
      .map((pc) => pc.company.id);
    return toReturn.filter(
      (n, i) => toReturn.findIndex((n1) => n === n1) === i
    );
  }

  static multipleInvolvedCompanies(project: Project): boolean {
    if (
      project == null ||
      project.projectContacts == null ||
      project.projectContacts.length <= 1
    ) {
      return false;
    }

    let seenCompanies = new Set<number>();
    for (let i = 0; i < project.projectContacts.length; i++) {
      const pc = project.projectContacts[i];
      if (!seenCompanies.has(pc.company.id)) {
        seenCompanies.add(pc.company.id);
      }
      if (seenCompanies.size > 1) {
        return true;
      }
    }

    return false;
  }

  static getAtLeastOneRetailer(projectContacts: ProjectContact[]): boolean {
    return !CollectionHelper.isPopulated(projectContacts)
      ? false
      : projectContacts.some(
          (pc) => pc.company != null && pc.company.isRetailer
        );
  }

  static getInvolvedSubprojectUsers(
    subProject: SubProject,
    project?: Project
  ): User[] {
    return subProject.type === 'BaseProject'
      ? this.getInvolvedBaseProjectUsers(<BaseProject>subProject, project)
      : this.getInvolvedAdjustmentUsers(<Adjustment>subProject);
  }

  private static getInvolvedBaseProjectUsers(
    baseProject: BaseProject,
    project?: Project
  ): User[] {
    const userMap = new Map<number, User>();
    if (
      baseProject.quotationHandler != null &&
      baseProject.quotationHandler.user != null
    ) {
      userMap.set(
        baseProject.quotationHandler.user.id,
        baseProject.quotationHandler.user
      );
    }
    if (
      baseProject.projectPlacement != null &&
      baseProject.projectPlacement.employee != null &&
      baseProject.projectPlacement.employee.user != null
    ) {
      userMap.set(
        baseProject.projectPlacement.employee.user.id,
        baseProject.projectPlacement.employee.user
      );
    }
    const inputProject = project == null ? baseProject.project : project;
    if (
      inputProject != null &&
      CollectionHelper.isPopulated(inputProject.products)
    ) {
      inputProject.products.forEach((p) => {
        if (p.designer != null && p.designer.user != null) {
          userMap.set(p.designer.user.id, p.designer.user);
        }
        if (p.printworkHandler != null && p.printworkHandler.user != null) {
          userMap.set(p.printworkHandler.user.id, p.printworkHandler.user);
        }
        if (p.finisher != null && p.finisher.user != null) {
          userMap.set(p.finisher.user.id, p.finisher.user);
        }
        if (
          p.productPlacement != null &&
          CollectionHelper.isPopulated(p.productPlacement.placers)
        ) {
          p.productPlacement.placers.forEach((placer) => {
            if (placer != null && placer.user != null) {
              userMap.set(placer.user.id, placer.user);
            }
          });
        }
      });
    }
    const toReturn: User[] = [];
    userMap.forEach((user) => {
      toReturn.push(user);
    });
    return toReturn;
  }

  private static getInvolvedAdjustmentUsers(adjustment: Adjustment): User[] {
    const userMap = new Map<number, User>();
    if (
      adjustment.quotationHandler != null &&
      adjustment.quotationHandler.user != null
    ) {
      userMap.set(
        adjustment.quotationHandler.user.id,
        adjustment.quotationHandler.user
      );
    }
    if (
      adjustment.projectPlacement != null &&
      adjustment.projectPlacement.employee != null &&
      adjustment.projectPlacement.employee.user != null
    ) {
      userMap.set(
        adjustment.projectPlacement.employee.user.id,
        adjustment.projectPlacement.employee.user
      );
    }
    if (
      adjustment.adjustmentDesigner != null &&
      adjustment.adjustmentDesigner.user != null
    ) {
      userMap.set(
        adjustment.adjustmentDesigner.user.id,
        adjustment.adjustmentDesigner.user
      );
    }
    if (
      adjustment.adjustmentPrintworkHandler != null &&
      adjustment.adjustmentPrintworkHandler.user != null
    ) {
      userMap.set(
        adjustment.adjustmentPrintworkHandler.user.id,
        adjustment.adjustmentPrintworkHandler.user
      );
    }
    if (
      adjustment.adjustmentFinisher != null &&
      adjustment.adjustmentFinisher.user != null
    ) {
      userMap.set(
        adjustment.adjustmentFinisher.user.id,
        adjustment.adjustmentFinisher.user
      );
    }
    if (
      adjustment.projectPlacement != null &&
      CollectionHelper.isPopulated(adjustment.projectPlacement.placers)
    ) {
      adjustment.projectPlacement.placers.forEach((p) => {
        if (p.user != null && p.user.id != null) {
          userMap.set(p.user.id, p.user);
        }
      });
    }
    const toReturn: User[] = [];
    userMap.forEach((user) => {
      toReturn.push(user);
    });
    return toReturn;
  }

  static isAdjustmentDesignAsset(productAsset: ProductAsset): boolean {
    return (
      productAsset != null &&
      this.ADJUSTMENT_DESIGN_ASSETS.includes(productAsset.formSection)
    );
  }

  static getTotalProjectExecutionMinutes(
    projectPlacement: ProjectPlacement
  ): number {
    if (!CollectionHelper.isPopulated(projectPlacement.registeredActions)) {
      return 0;
    }
    // only count start actions that are eventually followed by a stop action without another start action intervening
    let sum = 0;
    let currentAction = null;
    for (let i = projectPlacement.registeredActions.length - 1; i >= 0; i--) {
      const action = projectPlacement.registeredActions[i];
      if (action.type != null) {
        if (action.type.ordinal === 0) {
          currentAction = action;
        } else if (action.type.ordinal === 1 && currentAction != null) {
          sum += moment(action.date).diff(currentAction.date, 'minutes');
          currentAction = null;
        }
      }
    }
    return sum;
  }

  static getSoldRentedTextSuggestion(s: string): string {
    const test = s == null ? null : s.trim().toLowerCase();
    switch (test) {
      case 'te koop':
        return 'Verkocht';
      case 'te huur':
        return 'Verhuurd';
      case 'te koop/te huur':
        return 'Verkocht/Verhuurd';
      case 'a vendre':
        return 'Vendu';
      case 'a louer':
        return 'Loué';
      default:
        return '';
    }
  }

  static isIPBoardProject(project: Project | ProjectDto): boolean {
    return (
      project != null &&
      CollectionHelper.isPopulated(project.products) &&
      project.products.some((p) => ProductHelper.isIPBoard(p))
    );
  }

  static mustHaveAppointment(projectPlacement: ProjectPlacement): boolean {
    return (
      projectPlacement.placementFloor != null &&
      !this.PLACEMENT_FLOOR_APPOINTMENT_NOT_OBLIGATORY.includes(
        projectPlacement.placementFloor
      )
    );
  }

  static mustHaveAppointmentForPlacementFloor(placementFloor: string) {
    return (
      placementFloor != null &&
      !this.PLACEMENT_FLOOR_APPOINTMENT_NOT_OBLIGATORY.includes(placementFloor)
    );
  }

  static alwaysShowCollectKeyForProject(project: Project): boolean {
    return (
      CollectionHelper.isPopulated(project.products) &&
      project.products.some((p) => {
        if (!ProductHelper.isIPBoard(p)) {
          return false;
        }
        const ipBoard = <IPBoard>p;
        return (
          this.alwaysShowCollectKeyForIPBoardPart(ipBoard.boardPart) ||
          this.alwaysShowCollectKeyForIPBoardPart(ipBoard.otherSideBoardPart)
        );
      })
    );
  }

  private static alwaysShowCollectKeyForIPBoardPart(
    ipBoardPart: IPBoardPart
  ): boolean {
    return (
      ipBoardPart != null &&
      ipBoardPart.placementType != null &&
      (ipBoardPart.placementType.ordinal ===
        IPBoardPlacementTypeChoices.ATTACHED_TO_FACADE.ordinal ||
        ipBoardPart.placementType.ordinal ===
          IPBoardPlacementTypeChoices.BALCONY.ordinal ||
        ipBoardPart.placementType.ordinal ===
          IPBoardPlacementTypeChoices.STRETCHED.ordinal)
    );
  }

  static hasApprovableAddress(subProject: SubProject): boolean {
    return (
      subProject.projectPlacement != null &&
      subProject.projectPlacement.address != null &&
      !StringHelper.isEmptyOrWhiteSpace(
        subProject.projectPlacement.address.line1
      ) &&
      !StringHelper.isEmptyOrWhiteSpace(
        subProject.projectPlacement.address.postalCode
      ) &&
      !StringHelper.isEmptyOrWhiteSpace(
        subProject.projectPlacement.address.city
      )
    );
  }

  static calculateCollectKeyFromAddress(
    subProject: SubProject,
    printAddressPipe: PrintAddressPipe,
    project?: Project | ProjectDto
  ) {
    if (project == null) {
      project = subProject.project;
    }
    if (
      subProject.projectPlacement != null &&
      subProject.projectPlacement.collectKey &&
      (subProject.projectPlacement.collectKeyFrom == -1 ||
        subProject.projectPlacement.collectKeyFrom == null) &&
      !StringHelper.isEmpty(subProject.projectPlacement.collectKeyExtraInfo)
    ) {
      return subProject.projectPlacement.collectKeyExtraInfo;
    } else {
      let address = <any>(
        ProjectHelper.getKeyCollectionAddress(subProject, project)
      );
      if (address != null && address.address != null) {
        address = address.address;
      }

      return address == null
        ? null
        : printAddressPipe.transform(
            address.line1,
            address.postalCode,
            address.city
          );
    }
  }

  static getKeyCollectionAddress(
    subproject: SubProject,
    project?: Project | ProjectDto
  ): AddressWithType {
    if (project == null) {
      project = subproject.project;
    }
    if (
      subproject.projectPlacement == null ||
      !subproject.projectPlacement.collectKey ||
      project == null ||
      !CollectionHelper.isPopulated(project.projectContacts)
    ) {
      return null;
    }
    const projectContact = project.projectContacts.find(
      (pc) =>
        pc.company != null &&
        pc.company.id === subproject.projectPlacement.collectKeyFrom
    );
    return projectContact == null || projectContact.company == null
      ? null
      : AddressHelper.getAddressPreferring(
          projectContact.company.addresses,
          subproject.projectPlacement.collectKeyAddressType
        );
  }

  static getIsExecuted(
    subproject: SubProject,
    project?: Project | ProjectDto
  ): boolean {
    if (project == null) {
      project = subproject.project;
    }
    return subproject.type === 'BaseProject'
      ? ProjectHelper.getIsBaseProjectExecuted(subproject, project)
      : ProjectHelper.getIsAdjustmentExecuted(subproject);
  }

  private static getIsBaseProjectExecuted(
    subproject: SubProject,
    project: Project | ProjectDto
  ): boolean {
    if (project == null) {
      return false;
    }
    if (subproject.withPlacement) {
      return !CollectionHelper.isPopulated(project.products)
        ? false
        : project.products.every(
            (p) =>
              p.productPlacement != null && p.productPlacement.executionComplete
          );
    } else {
      return subproject.pickup == null ? false : subproject.pickup.ready;
    }
  }

  private static getIsAdjustmentExecuted(subproject: SubProject): boolean {
    if (subproject.withPlacement) {
      return subproject.projectPlacement == null
        ? false
        : subproject.projectPlacement.placementDone;
    } else {
      return subproject.pickup == null ? false : subproject.pickup.ready;
    }
  }

  static getSubProjectNullableExecutionState(
    subProject: SubProject,
    project?: Project | ProjectDto
  ): boolean {
    project = project == null ? subProject.project : project;
    if (subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      if (project != null && CollectionHelper.isPopulated(project.products)) {
        let intermediate: boolean = null;
        for (const product of project.products) {
          if (product.productPlacement != null) {
            intermediate = product.productPlacement.executionComplete;
            if (intermediate) {
              return true;
            } else if (intermediate === false) {
              return false;
            }
          }
        }
        return intermediate;
      }
    } else {
      if (subProject.projectPlacement != null) {
        if (subProject.projectPlacement.placementDone) {
          return true;
        }
        return subProject.projectPlacement.placementDone === false
          ? false
          : null;
      }
    }
  }

  public static calculateNeedExternalSupplier(
    subproject: SubProject,
    project?: Project | ProjectDto
  ): void {
    if (project == null) {
      project = subproject.project;
    }

    if (subproject.type === 'BaseProject') {
      this.calculateBaseProjectNeedExternalSupplier(
        <BaseProject>subproject,
        project
      );
    } else {
      this.calculateAdjustmentNeedExternalSupplier(<Adjustment>subproject);
    }
  }

  private static calculateBaseProjectNeedExternalSupplier(
    baseProject: BaseProject,
    project: Project | ProjectDto
  ): void {
    if (project == null || !CollectionHelper.isPopulated(project.products)) {
      return;
    }

    let needExternalSupplier: boolean | null = null;

    for (const product of project.products) {
      if (product.productPlacement == null) {
        continue;
      }

      needExternalSupplier =
        needExternalSupplier || product.productPlacement.extraHoogwerker;
      if (
        product.productPlacement.extraHoogwerker &&
        !product.productPlacement.extraHoogwerkerDone
      ) {
        baseProject.needExternalSupplier = true;
        return;
      }

      needExternalSupplier =
        needExternalSupplier || product.productPlacement.parkingBan;
      if (
        product.productPlacement.parkingBan &&
        !product.productPlacement.parkingBanDone
      ) {
        baseProject.needExternalSupplier = true;
        return;
      }

      needExternalSupplier =
        needExternalSupplier || product.productPlacement.needExtraSupplier;
      if (
        product.productPlacement.needExtraSupplier &&
        !product.productPlacement.needExtraSupplierDone
      ) {
        baseProject.needExternalSupplier = true;
        return;
      }

      needExternalSupplier =
        needExternalSupplier || product.productPlacement.needExtraMaterials;
      if (
        product.productPlacement.needExtraMaterials &&
        !product.productPlacement.needExtraMaterialsDone
      ) {
        baseProject.needExternalSupplier = true;
        return;
      }
    }

    baseProject.needExternalSupplier =
      needExternalSupplier == null ? null : false;
  }

  private static calculateAdjustmentNeedExternalSupplier(
    adjustment: Adjustment
  ): void {
    let needExternalSupplier: boolean | null = null;

    needExternalSupplier = needExternalSupplier || adjustment.extraHoogwerker;
    if (adjustment.extraHoogwerker && !adjustment.extraHoogwerkerDone) {
      adjustment.needExternalSupplier = true;
      return;
    }

    needExternalSupplier = needExternalSupplier || adjustment.parkingBan;
    if (adjustment.parkingBan && !adjustment.parkingBanDone) {
      adjustment.needExternalSupplier = true;
      return;
    }

    needExternalSupplier = needExternalSupplier || adjustment.needExtraSupplier;
    if (adjustment.needExtraSupplier && !adjustment.needExtraSupplierDone) {
      adjustment.needExternalSupplier = true;
      return;
    }

    needExternalSupplier =
      needExternalSupplier || adjustment.needExtraMaterials;
    if (adjustment.needExtraMaterials && !adjustment.needExtraMaterialsDone) {
      adjustment.needExternalSupplier = true;
      return;
    }

    adjustment.needExternalSupplier =
      needExternalSupplier == null ? null : false;
  }

  public static hasPlacer(subProject: SubProject): boolean {
    return subProject.type === 'BaseProject'
      ? this.hasPlacerBaseProject(subProject)
      : this.hasPlacerAdjustment(subProject);
  }

  private static hasPlacerBaseProject(subProject: SubProject): boolean {
    return subProject.project == null
      ? false
      : !CollectionHelper.isPopulated(subProject.project.products)
      ? false
      : subProject.project.products.some(
          (p) =>
            p.productPlacement != null &&
            CollectionHelper.isPopulated(p.productPlacement.placers)
        );
  }

  private static hasPlacerAdjustment(subProject: SubProject): boolean {
    return subProject.projectPlacement == null
      ? false
      : CollectionHelper.isPopulated(subProject.projectPlacement.placers);
  }

  public static getFirstPlacer(subProject: SubProject): Employee {
    return subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
      ? this.getFirstBaseProjectPlacer(subProject)
      : this.getFirstAdjustmentPlacer(subProject);
  }

  static getFirstBaseProjectPlacer(subProject: SubProject): Employee {
    if (
      subProject.project == null ||
      !CollectionHelper.isPopulated(subProject.project.products)
    ) {
      return null;
    }
    for (const product of subProject.project.products) {
      if (
        product.productPlacement == null ||
        !CollectionHelper.isPopulated(product.productPlacement.placers)
      ) {
        continue;
      }
      return product.productPlacement.placers[0];
    }
    return null;
  }

  static getFirstAdjustmentPlacer(subProject: SubProject): Employee {
    return subProject.projectPlacement == null ||
      !CollectionHelper.isPopulated(subProject.projectPlacement.placers)
      ? null
      : subProject.projectPlacement.placers[0];
  }

  static getPlacerNames(subProject: SubProject, project: Project): string {
    if (this.isIPBoardProject(project)) {
      return subProject.tourItem == null ||
        subProject.tourItem.tour == null ||
        subProject.tourItem.tour.placer == null
        ? ''
        : EmployeeHelper.getName(subProject.tourItem.tour.placer);
    } else {
      if (subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
        return ProductHelper.getPlacerNames(project.products);
      } else {
        return subProject.projectPlacement == null
          ? ''
          : EmployeeHelper.getNames(subProject.projectPlacement.placers);
      }
    }
  }

  public static getTimeBudgetMinutes(subProject: SubProject): number {
    return subProject.type === 'BaseProject'
      ? this.getTimeBudgetMinutesBaseProject(subProject)
      : this.getTimeBudgetMinutesAdjustment(subProject);
  }

  private static getTimeBudgetMinutesBaseProject(
    subProject: SubProject
  ): number {
    if (
      subProject.project == null ||
      !CollectionHelper.isPopulated(subProject.project.products)
    ) {
      return 0;
    }

    let timeBudgetMinutes = 0;
    subProject.project.products.forEach((p) => {
      timeBudgetMinutes +=
        (p.productPlacement.installerHourBudget == null
          ? 0
          : p.productPlacement.installerHourBudget * 60) +
        (p.productPlacement.installerMinuteBudget == null
          ? 0
          : p.productPlacement.installerMinuteBudget);
    });
    return timeBudgetMinutes;
  }

  private static getTimeBudgetMinutesAdjustment(
    subProject: SubProject
  ): number {
    return subProject.projectPlacement == null
      ? 0
      : (subProject.projectPlacement.installerHourBudget == null
          ? 0
          : subProject.projectPlacement.installerHourBudget * 60) +
          (subProject.projectPlacement.installerMinuteBudget == null
            ? 0
            : subProject.projectPlacement.installerMinuteBudget);
  }

  public static getTimeBudgetInHoursAndMinutes(subProject: SubProject): string {
    const timeBudgetMinutes = this.getTimeBudgetMinutes(subProject);
    const hours = Math.floor(timeBudgetMinutes / 60);
    const minutes = timeBudgetMinutes % 60;
    return StringHelper.join(
      [
        hours === 0 ? null : `${hours} uur`,
        minutes === 0
          ? null
          : `${minutes} ${minutes === 1 ? 'minuut' : 'minuten'}`,
      ],
      ' '
    );
  }

  public static displayImmoscoopOptionForProjectContacts(
    projectContacts: ProjectContact[]
  ): boolean {
    return (
      CollectionHelper.isPopulated(projectContacts) &&
      projectContacts.some((pc) => pc.company != null && pc.company.immoscoop)
    );
  }

  public static displaySpottoOptionForProjectContacts(
    projectContacts: ProjectContact[]
  ): boolean {
    return (
      CollectionHelper.isPopulated(projectContacts) &&
      projectContacts.some((pc) => pc.company != null && pc.company.spotto)
    );
  }

  public static showStoreQuestion(
    subProject: SubProject,
    project?: Project | ProjectDto
  ): boolean {
    if (project == null) {
      project = subProject.project;
    }
    return (
      project != null &&
      subProject.type === SUB_PROJECT_TYPES.REPO &&
      CollectionHelper.isPopulated(project.products) &&
      project.products.some((p) => p.type === ProductTypes.BOARD.value)
    );
  }

  public static baseProjectHasSecondBoard(baseProject: BaseProject): boolean {
    return (
      baseProject.projectPlacement.secondBoard != null &&
      (baseProject.projectPlacement.secondBoard.secondBoardCopy != null ||
        !StringHelper.isEmpty(
          baseProject.projectPlacement.secondBoard.secondBoardConfiguration
        ) ||
        !StringHelper.isEmpty(
          baseProject.projectPlacement.secondBoard.secondBoardPlacementInfo
        ))
    );
  }

  static getMailSubjectLine(project: Project): string {
    const toReturn = 'Project #' + project.id;
    return this.getBaseReturn(toReturn, project);
  }

  static getAdjustmentMailSubjectLine(
    subProject: SubProject,
    project: Project
  ): string {
    if (project == null) {
      return '';
    }
    const toReturn =
      ProjectHelper.getFormDisplayNameUpperCase(subProject) +
      ' project #' +
      project.id;
    return this.getBaseReturn(toReturn, project);
  }

  private static getBaseReturn(baseReturn: string, project: Project): string {
    const baseProject = ProjectHelper.getBaseProject(project);
    if (
      baseProject != null &&
      baseProject.projectPlacement != null &&
      baseProject.projectPlacement.address != null
    ) {
      const addressPart = StringHelper.join(
        [
          baseProject.projectPlacement.address.line1,
          StringHelper.join(
            [
              baseProject.projectPlacement.address.postalCode,
              baseProject.projectPlacement.address.city,
            ],
            ' '
          ),
        ],
        ', '
      );
      if (!StringHelper.isEmpty(addressPart)) {
        return baseReturn + ' / ' + addressPart;
      }
    }
    return baseReturn;
  }
}
